/**
 * Tenant application — learner and tenant-admin, one codebase for every tenant.
 *
 * Learner and admin share a shell, a router and the design system; only the palette
 * and the enabled surfaces differ, and both arrive as data. Adding a tenant adds no
 * code here, which is the whole point.
 */

import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import type { Session } from './shared/lib/session';
import { personaOf } from './shared/lib/session';
import { getConfig, surfaceMode } from './shared/lib/config';
import { Shell } from './shared/layout/Shell';
import { RequirePersona } from './shared/layout/RequirePersona';
import { ExternalSurface } from './shared/layout/ExternalSurface';

import Landing from './learner/routes/Landing';
import Login from './learner/auth/Login';
import Register from './learner/auth/Register';
import Discover from './learner/routes/Discover';
import Paths from './learner/routes/Paths';
import PathDetail from './learner/routes/PathDetail';
import Dashboard from './learner/routes/Dashboard';
import CourseHome from './learner/routes/CourseHome';
import Unit from './learner/routes/Unit';
import Profile from './learner/routes/Profile';
import Settings from './learner/routes/Settings';
import EnrolmentSuccess from './learner/routes/EnrolmentSuccess';
import PurchaseComplete from './learner/routes/PurchaseComplete';
import MyCertificates from './learner/routes/MyCertificates';
import MyGrades from './learner/routes/MyGrades';
import ActivateAccount from './learner/routes/ActivateAccount';
import CertificateView from './learner/routes/CertificateView';
import { AdminShell } from './admin/AdminShell';
import AdminHome from './admin/routes/AdminHome';
import AdminCourses from './admin/routes/AdminCourses';
import { CourseLayout } from './admin/routes/CourseLayout';
import CourseOutline from './admin/routes/CourseOutline';
import CourseUnit from './admin/routes/CourseUnit';
import CourseSettings from './admin/routes/CourseSettings';
import CourseGrading from './admin/routes/CourseGrading';
import CourseCertificates from './admin/routes/CourseCertificates';
import AdminPaths from './admin/routes/AdminPaths';
import AdminLibrary from './admin/routes/AdminLibrary';
import LibraryDetail from './admin/routes/LibraryDetail';
import AdminLearners from './admin/routes/AdminLearners';
import AdminPeople from './admin/people/AdminPeople';
import PersonDetail from './admin/people/PersonDetail';
import AdminTeam from './admin/routes/AdminTeam';
import AdminEnrolments from './admin/routes/AdminEnrolments';
import AdminDiscussions from './admin/routes/AdminDiscussions';
import AdminAnnouncements from './admin/routes/AdminAnnouncements';
import AdminProgress from './admin/routes/AdminProgress';
import AdminGrades from './admin/routes/AdminGrades';
import AdminCertificates from './admin/routes/AdminCertificates';
import AdminSettings from './admin/routes/AdminSettings';
import AdminPayments from './admin/routes/AdminPayments';
import AdminPromotions from './admin/routes/AdminPromotions';
import { AdminOrderDetail } from './admin/routes/AdminOrderDetail';
import PaymentAudit from './admin/routes/PaymentAudit';
import {
  LegacyAuthoringRedirect,
  LegacyContainerRedirect,
} from './admin/LegacyAuthoringRedirect';

/**
 * Wrap a page in its configured surface mode. `own` renders it, `external` bounces
 * to the upstream Open edX app, `off` hides it. This is the switch that lets the
 * thin app ship partially: unbuilt surfaces redirect rather than 404.
 */
function surface(name: string, element: React.ReactElement) {
  const mode = surfaceMode(name);
  if (mode === 'own' || mode === 'embedded') return element;
  if (mode === 'external') return <ExternalSurface surface={name} />;
  return <Navigate to="/" replace />;
}

export function TenantApp({ session }: { session: Session }) {
  const config = getConfig();
  const persona = personaOf(session, config.tenant.orgs);

  const router = createBrowserRouter([
    // The public landing page sits OUTSIDE the shell. It carries its own transparent
    // overlay nav across a gradient hero, and layering the shell's sticky white header
    // on top of that destroys the effect. Signed-in visitors skip it entirely — they
    // want their workspace, not the shop window.
    {
      path: '/',
      element: session.authenticated ? (
        <Navigate to="/dashboard" replace />
      ) : (
        surface('discover', <Landing />)
      ),
    },
    // Admin has its own shell: a sidebar layout, not the learner header/footer. It
    // carries a dozen sections, which a horizontal bar cannot hold without hiding
    // where you are behind a dropdown.
    {
      path: '/admin',
      element: (
        <RequirePersona min="tenant_staff" persona={persona}>
          <AdminShell session={session} persona={persona} />
        </RequirePersona>
      ),
      children: [
        { index: true, element: <AdminHome /> },
        { path: 'courses', element: <AdminCourses /> },
        // Course authoring. The outline is the course's home rather than a tab, because
        // Verawood ships no outline page of its own — Studio's template was removed when
        // authoring moved to a micro-frontend, so without this a course has nowhere to
        // put content.
        //
        // Course keys and usage keys are URL-ENCODED into these segments: both contain
        // ':' and '+', and an unencoded '+' is read as a space once decoded, which turns
        // a valid key into an invalid one at the far end.
        {
          path: 'courses/:courseId',
          element: <CourseLayout />,
          children: [
            { index: true, element: <CourseOutline /> },
            { path: 'settings', element: <CourseSettings /> },
            { path: 'grading', element: <CourseGrading /> },
            { path: 'certificates', element: <CourseCertificates /> },
          ],
        },
        // Outside the layout on purpose: a unit is content inside the outline, not a
        // sibling of "Grading", and it carries its own breadcrumb.
        { path: 'courses/:courseId/units/:unitId', element: <CourseUnit /> },
        { path: 'paths', element: <AdminPaths /> },
        { path: 'library', element: <AdminLibrary /> },
        { path: 'libraries/:libraryId', element: <LibraryDetail /> },
        // People is one module. `learners` and `team` are the same page with a preset
        // filter, kept as their own paths so existing links and the sidebar still work.
        { path: 'people', element: <AdminPeople /> },
        { path: 'people/:username', element: <PersonDetail /> },
        { path: 'learners', element: <AdminLearners /> },
        { path: 'team', element: <AdminTeam /> },
        { path: 'enrolments', element: <AdminEnrolments /> },
        { path: 'discussions', element: <AdminDiscussions /> },
        { path: 'announcements', element: <AdminAnnouncements /> },
        { path: 'progress', element: <AdminProgress /> },
        { path: 'grades', element: <AdminGrades /> },
        { path: 'certificates', element: <AdminCertificates /> },
        // Payment operations: refunds, access changes and the permanent audit trail.
        //
        // `payments/audit` is declared BEFORE `payments/:orderRef`, because an order ref is
        // an opaque string that matches anything — a dynamic segment listed first would
        // swallow `/admin/payments/audit` and try to load an order called "audit", which
        // 404s with a message about an order nobody asked for.
        //
        // The order ref is URL-ENCODED into the segment for the same reason course keys are:
        // `orders.new_order_id` is not constrained to slug characters.
        { path: 'payments', element: <AdminPayments /> },
        // Discount and access codes. Under `promotions` rather than under `payments/...`
        // because an ACCESS code makes no Stripe call and belongs to no order — filing it
        // beneath the order screens would imply it does.
        { path: 'promotions', element: <AdminPromotions /> },
        { path: 'payments/audit', element: <PaymentAudit /> },
        { path: 'payments/:orderRef', element: <AdminOrderDetail /> },
        { path: 'settings', element: <AdminSettings /> },
      ],
    },
    // Links the platform itself generates use the authoring micro-frontend's URL shape.
    // They arrive here because our Tutor plugin points COURSE_AUTHORING_MICROFRONTEND_URL
    // at this app; these two routes translate them into ours. Outside the shell, since
    // they only ever redirect.
    { path: 'course/:courseId/*', element: <LegacyAuthoringRedirect /> },
    { path: 'container/:blockId', element: <LegacyContainerRedirect /> },
    {
      element: <Shell session={session} persona={persona} />,
      children: [
        { path: 'discover', element: surface('discover', <Discover />) },
        // Learning paths. PUBLIC, like the catalogue: the pages render for a signed-out
        // visitor in their browse state, which is the state someone deciding whether to
        // join actually sees. (What that state SAYS differs — the pathways API is
        // IsAuthenticated on every read, so the page asks them to sign in rather than
        // claiming the academy has none. See `PATHS_REQUIRE_SIGN_IN`.)
        //
        // NOT wrapped in `surface()`. The header gates the LINK on the `learning_paths`
        // feature flag — which defaults to TRUE, now that the pathways backend is installed
        // and `shared/lib/paths.ts` reads it — but a flag governing whether a link appears
        // is not the same as a route that redirects to '/'. Leaving the routes reachable is
        // what lets a tenant who has switched the flag off still reach a path by its URL,
        // and what keeps a shared link working.
        { path: 'paths', element: <Paths /> },
        { path: 'paths/:pathSlug', element: <PathDetail /> },
        // Auth pages live OUTSIDE the shell's signed-in composition but inside the
        // router, so a learner keeps their place via ?next= after signing in.
        { path: 'login', element: <Login /> },
        { path: 'register', element: <Register /> },
        { path: 'courses/:courseId', element: surface('course_about', <CourseHome />) },
        // The step between enrolling and starting. Nested under the course so the key is
        // already in the path and the screen needs no query string to know what it confirms.
        {
          path: 'courses/:courseId/enrolled',
          element: (
            <RequirePersona min="learner" persona={persona}>
              <EnrolmentSuccess />
            </RequirePersona>
          ),
        },
        /*
         * WHERE STRIPE RETURNS A LEARNER TO, AND THESE TWO PATHS ARE NOT THIS FILE'S CHOICE.
         *
         * `openedx_thinlms_payments/checkout.py:_success_path()` is
         * `/purchase/complete?order={order_id}` and `_cancel_path()` is
         * `/purchase/cancelled?order={order_id}`. Those URLs are registered with Stripe as the
         * Session's `success_url` and `cancel_url` at the moment a learner clicks Buy, on the
         * academy's own domain, so they must exist here EXACTLY — a rename on either side
         * strands a paying learner on a blank page, which is what was happening: neither route
         * existed at all, and every completed payment landed on the app shell with no content.
         *
         * BEHIND `RequirePersona min="learner"` because the order read is scoped to
         * `request.user`: a signed-out visitor has no order to see, and sending them through
         * login with `?next=` returns them here with the reference intact.
         *
         * ONE COMPONENT, TWO ROUTES. Both read the same order and render whatever the server
         * says it is in — the cancel path is a HINT about how the learner arrived, not a verdict,
         * because Stripe sends a learner to `cancel_url` when they close its page and the payment
         * can still be `pending` on a method that settles slowly.
         */
        {
          path: 'purchase/complete',
          element: (
            <RequirePersona min="learner" persona={persona}>
              <PurchaseComplete />
            </RequirePersona>
          ),
        },
        {
          path: 'purchase/cancelled',
          element: (
            <RequirePersona min="learner" persona={persona}>
              <PurchaseComplete cancelled />
            </RequirePersona>
          ),
        },
        // The header has always linked to these two; until now they rendered nothing.
        {
          path: 'u',
          element: (
            <RequirePersona min="learner" persona={persona}>
              <Profile />
            </RequirePersona>
          ),
        },
        {
          path: 'settings',
          element: (
            <RequirePersona min="learner" persona={persona}>
              <Settings />
            </RequirePersona>
          ),
        },
        {
          path: 'certificates',
          element: (
            <RequirePersona min="learner" persona={persona}>
              <MyCertificates />
            </RequirePersona>
          ),
        },
        // `:courseKey` rather than a splat, matching `courses/:courseId` above: a course
        // key's `:` and `+` are URL-ENCODED by `gradesHref` before they reach the link, so
        // the raw segment React Router matches never contains an unescaped `/`, and
        // `useParams()` decodes it back to `course-v1:ORG+NUM+RUN` on the way out.
        {
          path: 'grades/:courseKey',
          element: (
            <RequirePersona min="learner" persona={persona}>
              <MyGrades />
            </RequirePersona>
          ),
        },
        // ONE ISSUED CERTIFICATE, AND DELIBERATELY NOT BEHIND `RequirePersona`.
        //
        // This is the link a learner sends to an employer. Requiring a session would mean the
        // recipient is asked to sign in to an academy they have nothing to do with, and a
        // credential only its owner can open is not a credential anybody can check — so the
        // page and the endpoint behind it are both public. `certificates` above (the learner's
        // own list) stays gated; these are different pages with different audiences.
        //
        // It also replaces a path we used to hand to the platform: `/certificates/<uuid>` was
        // proxied to the LMS's own server-rendered certificate page. Nothing proxies it now,
        // so this route owns it, and links the platform generates from the tenant's
        // LMS_ROOT_URL land here.
        //
        // TWO KINDS, TWO NAMESPACES, ONE COMPONENT. `certificates/path/<uuid>` is declared
        // FIRST because `certificates/:uuid` would otherwise match it with uuid="path" —
        // React Router ranks a static segment above a dynamic one, so the order here is
        // belt to that brace and makes the intent readable rather than implicit.
        //
        // They are separate routes rather than one route with a query parameter because
        // they read different tables through different endpoints, and a shared link must
        // carry which one it is. A pathway uuid pasted into the course route 404s, which is
        // correct: it is not a course certificate.
        { path: 'certificates/path/:uuid', element: <CertificateView kind="pathway" /> },
        { path: 'certificates/:uuid', element: <CertificateView /> },
        // Where the activation email lands.
        //
        // NOT wrapped in `RequirePersona`, and that is the whole point: nobody can be
        // signed in yet — activating is what makes signing in possible — and the link is
        // usually opened in whatever browser the mail client hands it to, very often a
        // private window with no session at all. A persona guard here would redirect the
        // learner to a login they cannot yet pass.
        //
        // Before this route existed the tenant host answered `/activate/<key>` with the
        // app shell and a 200, so the account was never activated and nothing said so.
        // The email therefore had to point at the platform host instead, which put a
        // generic page in front of the first message a customer ever receives.
        { path: 'activate/:key', element: <ActivateAccount /> },
        {
          path: 'dashboard',
          element: (
            <RequirePersona min="learner" persona={persona}>
              {surface('dashboard', <Dashboard />)}
            </RequirePersona>
          ),
        },
        {
          path: 'learn/:courseId/:unitId?',
          element: (
            <RequirePersona min="learner" persona={persona}>
              {surface('unit_player', <Unit />)}
            </RequirePersona>
          ),
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
}
